import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Search, Plus, Pencil, Power, PowerOff, ShieldCheck } from 'lucide-react';
import { format } from 'date-fns/format';
import { UserFormModal } from './UserFormModal';
import type { UseUsersListReturn } from '../../hooks/useUsersList';

const getRoleBadgeVariant = (role: string) => {
  switch (role) {
    case 'owner':
      return 'default'; // dark
    case 'admin':
      return 'secondary';
    case 'manager':
      return 'outline';
    default:
      return 'outline';
  }
};

export const UsersList: React.FC<UseUsersListReturn> = ({
  loading,
  currentUser,
  statusFilter,
  setStatusFilter,
  roleFilter,
  setRoleFilter,
  searchName,
  setSearchName,
  searchEmail,
  setSearchEmail,
  currentPage,
  setCurrentPage,
  itemsPerPage,
  setItemsPerPage,
  filteredUsers,
  paginatedItems,
  totalPages,
  isModalOpen,
  setIsModalOpen,
  editingUser,
  formData,
  setFormData,
  fieldErrors,
  formErrors,
  submitting,
  handleOpenModal,
  handleSubmit,
  handleToggleStatus,
  canEditUser,
  canToggleUserStatus,
  canCreateUser,
}) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <h2 className="text-3xl tracking-tight">{t('admin.users.title')}</h2>

        {canCreateUser() && (
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button onClick={() => handleOpenModal()}>
              <Plus className="h-4 w-4 mr-2" />
              {t('admin.users.add')}
            </Button>
          </div>
        )}
      </div>

      <div className="flex flex-col sm:flex-row gap-4 bg-white p-4 rounded-lg border shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <input
            type="text"
              placeholder={t('admin.users.filterName')}
            className="pl-9 h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
          />
        </div>

        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-500" />
          <input
            type="text"
              placeholder={t('admin.users.filterEmail')}
            className="pl-9 h-10 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
            value={searchEmail}
            onChange={(e) => setSearchEmail(e.target.value)}
          />
        </div>

        <select
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          className="h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
        >
          <option value="all">{t('admin.users.filterRole')}</option>
          <option value="owner">{t('admin.users.owner')}</option>
          <option value="admin">{t('admin.users.roles.admin')}</option>
          <option value="manager">{t('admin.users.roles.manager')}</option>
        </select>

        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
        >
          <option value="all">{t('admin.users.filterStatus')}</option>
          <option value="active">{t('admin.users.active')}</option>
          <option value="inactive">{t('admin.users.inactive')}</option>
        </select>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t('admin.users.name')}</TableHead>
                <TableHead>{t('admin.users.email')}</TableHead>
                <TableHead>{t('admin.users.role')}</TableHead>
                <TableHead>{t('admin.users.active')}</TableHead>
                <TableHead>{t('admin.clients.registeredAt')}</TableHead>
                <TableHead className="text-right">{t('common.actions')}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10">
                    {t('admin.users.loading')}
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-10 text-muted-foreground">
                    {t('admin.users.noUsers')}
                  </TableCell>
                </TableRow>
              ) : (
                paginatedItems.map((user) => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {user.name}
                        {user.role === 'owner' && (
                          <span title={t('admin.users.owner')}>
                            <ShieldCheck className="h-4 w-4 text-blue-600" />
                          </span>
                        )}
                        {user.id === currentUser?.id && (
                          <Badge variant="outline" className="text-[10px] ml-1">
                            {t('admin.users.you')}
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      <Badge variant={getRoleBadgeVariant(user.role)}>
                        {t(`admin.users.roles.${user.role}`, user.role)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant={user.active ? 'default' : 'secondary'}>
                        {user.active ? t('admin.users.active') : t('admin.users.inactive')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {format(new Date(user.created_at), 'PPP')}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex gap-1 justify-end">
                        {canEditUser(user) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenModal(user)}
                            title={t('admin.users.edit')}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {canToggleUserStatus(user) && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleToggleStatus(user)}
                            title={
                              user.active
                                ? t('admin.users.deactivate')
                                : t('admin.users.reactivate')
                            }
                            className={
                              user.active
                                ? 'text-red-600 hover:text-red-700'
                                : 'text-green-600 hover:text-green-700'
                            }
                          >
                            {user.active ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t">
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.items_per_page')}:
              </span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="h-8 rounded-md border border-slate-300 bg-white px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {t('common.page_info', {
                  current: currentPage,
                  total: totalPages || 1,
                })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p: number) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                {t('common.previous')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setCurrentPage((p: number) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages || totalPages === 0}
              >
                {t('common.next')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <UserFormModal
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        editingUser={editingUser}
        currentUser={currentUser}
        formData={formData}
        setFormData={setFormData}
        fieldErrors={fieldErrors}
        formErrors={formErrors}
        onSubmit={handleSubmit}
        submitting={submitting}
      />
    </div>
  );
};
